/**
 * Character Card Component
 * MVP 16: Individual character card for selection grid
 *
 * Features:
 * - Card design: 3D preview with idle animation, name, status icon (✅/🔒/💎)
 * - Visual states:
 *   - Available: Full color, ✅ checkmark, clickable, hover scale 1.05
 *   - Locked Free: 80% opacity, 🔒 icon, tooltip "Sign Up to Unlock"
 *   - Locked Premium: Full color, gold border, 💎 icon + "$1.99"
 * - Click handlers for selection and unlock prompts
 */



export interface CharacterCardData {
  id: string;
  name: string;
  emoji?: string;
  modelPath: string;
  tier: 'no-auth' | 'free' | 'premium' | 'future';
  price?: number;
  isAvailable: boolean;
  isSelected?: boolean;
}

export interface CharacterCardOptions {
  onClick?: (characterId: string) => void;
  onPreview?: (characterId: string) => void;
}
import { SharedCharacterRenderer } from '../services/SharedCharacterRenderer';

export class CharacterCard {
  private container: HTMLElement;
  private data: CharacterCardData;
  private options: CharacterCardOptions;
  private card: HTMLElement | null = null;
  private canvas2d: HTMLCanvasElement | null = null;
  private viewId: string;

  constructor(container: HTMLElement, data: CharacterCardData, options: CharacterCardOptions = {}) {
    this.container = container;
    this.data = data;
    this.options = options;
    this.viewId = `card-view-${data.id}-${Math.random().toString(36).substr(2, 9)}`;
    this.render();
  }

  /**
   * Render the character card
   */
  private render(): void {
    this.card = document.createElement('div');
    this.card.className = 'character-card';

    // Base styles
    const baseStyles = `
      position: relative;
      background: linear-gradient(135deg, #2d5f2e 0%, #4a8f4d 100%);
      border-radius: 12px;
      padding: 16px;
      cursor: ${this.data.isAvailable ? 'pointer' : 'not-allowed'};
      transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      border: 3px solid ${this.getBorderColor()};
      opacity: ${this.getOpacity()};
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      min-height: 140px;
    `;

    this.card.style.cssText = baseStyles;

    // Selected state
    if (this.data.isSelected) {
      this.card.style.borderColor = '#FFD700';
      this.card.style.boxShadow = '0 8px 24px rgba(255, 215, 0, 0.6)';
    }

    // 2D Canvas for 3D Preview (Blitted from shared renderer)
    this.canvas2d = document.createElement('canvas');
    this.canvas2d.width = 200;
    this.canvas2d.height = 200;
    this.canvas2d.style.cssText = `
      width: 100%;
      height: 120px;
      margin-bottom: 4px;
      border-radius: 8px;
      overflow: hidden;
      background: rgba(0, 0, 0, 0.2);
    `;

    // Character name
    const name = document.createElement('div');
    name.style.cssText = `
      font-size: 16px;
      font-weight: 700;
      color: white;
      text-align: center;
      text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
    `;
    name.textContent = this.data.name;

    // Status indicator
    const status = document.createElement('div');
    status.style.cssText = `
      font-size: 24px;
      margin-top: 4px;
    `;
    status.textContent = this.getStatusIcon();

    // Price label for premium characters
    if (this.data.tier === 'premium' && this.data.price) {
      const price = document.createElement('div');
      price.style.cssText = `
        position: absolute;
        top: 8px;
        right: 8px;
        background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
        color: #2c5f2d;
        padding: 4px 8px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 700;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      `;
      price.textContent = `$${this.data.price.toFixed(2)}`;
      this.card.appendChild(price);
    }

    // Assemble card
    this.card.appendChild(this.canvas2d);
    this.card.appendChild(name);
    this.card.appendChild(status);

    // Add tooltip for locked characters
    if (!this.data.isAvailable) {
      this.card.title = this.getTooltipText();
    }

    // Event listeners
    this.setupEventListeners();

    // Append to container
    this.container.appendChild(this.card);

    // Register with shared renderer after a short delay to ensure DOM layout is complete
    requestAnimationFrame(() => this.initSharedPreview());
  }

  private async initSharedPreview(): Promise<void> {
    if (!this.canvas2d) return;

    // Set internal resolution based on actual display size for correct aspect ratio
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas2d.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      this.canvas2d.width = rect.width * dpr;
      this.canvas2d.height = rect.height * dpr;
    } else {
      // Fallback
      this.canvas2d.width = 400;
      this.canvas2d.height = 240;
    }

    const randomRotation = Math.random() * Math.PI * 2;
    await SharedCharacterRenderer.getInstance().register(
      this.viewId,
      this.data.id,
      this.canvas2d,
      {
        rotationSpeed: 0.003,
        autoRotate: true,
        showAnimation: true,
        cameraDistance: 2,
        initialRotation: randomRotation
      }
    );
  }

  /**
   * Cleanup and destroy the card
   */
  public destroy(): void {
    SharedCharacterRenderer.getInstance().unregister(this.viewId);
    if (this.card) {
      this.card.remove();
      this.card = null;
    }
  }

  /**
   * Get border color based on tier and availability
   */
  private getBorderColor(): string {
    if (this.data.isSelected) {
      return '#FFD700';
    }
    if (this.data.tier === 'premium') {
      return 'rgba(255, 215, 0, 0.8)';
    }
    if (this.data.isAvailable) {
      return 'rgba(255, 215, 0, 0.4)';
    }
    return 'rgba(255, 255, 255, 0.3)';
  }

  /**
   * Get opacity based on availability
   */
  private getOpacity(): string {
    if (this.data.tier === 'premium') {
      return '1'; // Premium always full opacity to show it's available for purchase
    }
    if (!this.data.isAvailable) {
      return '0.6'; // Locked free characters are dimmed
    }
    return '1';
  }

  /**
   * Get status icon (✅/🔒/💎)
   */
  private getStatusIcon(): string {
    if (this.data.isAvailable) {
      return '✅';
    }
    if (this.data.tier === 'premium') {
      return '💎';
    }
    return ''; // No lock icon, just dimmed
  }

  /**
   * Get tooltip text for locked characters
   */
  private getTooltipText(): string {
    if (this.data.tier === 'premium') {
      return 'Premium Character - Coming Soon!';
    }
    return 'Create account to use';
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    if (!this.card) return;

    // Click handler
    this.card.addEventListener('click', () => {
      if (this.data.isAvailable) {
        this.options.onClick?.(this.data.id);
      } else {
        // Show preview or unlock prompt
        this.options.onPreview?.(this.data.id);
      }
    });

    // Hover effects (only for available characters)
    if (this.data.isAvailable) {
      this.card.addEventListener('mouseenter', () => {
        if (this.card) {
          this.card.style.transform = 'scale(1.05)';
          this.card.style.boxShadow = '0 8px 24px rgba(255, 215, 0, 0.5)';
        }
      });

      this.card.addEventListener('mouseleave', () => {
        if (this.card) {
          this.card.style.transform = 'scale(1)';
          this.card.style.boxShadow = this.data.isSelected
            ? '0 8px 24px rgba(255, 215, 0, 0.6)'
            : '0 4px 12px rgba(0, 0, 0, 0.3)';
        }
      });
    }
  }

  /**
   * Update selected state
   */
  public setSelected(selected: boolean): void {
    this.data.isSelected = selected;
    if (this.card) {
      if (selected) {
        this.card.style.borderColor = '#FFD700';
        this.card.style.boxShadow = '0 8px 24px rgba(255, 215, 0, 0.6)';
      } else {
        this.card.style.borderColor = this.getBorderColor();
        this.card.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
      }
    }
  }
}
